import { AllEnterpriseModule, LicenseManager, ModuleRegistry } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';

import { FlexBox } from '@blockworks/ui/flex-box';
import { Input } from '@blockworks/ui/forms/input';
import { cn } from '@blockworks/ui/system';

import { TABLE_FOOTER_HEIGHT, TABLE_FOOTER_HTML_ID, VisualizationTableProps } from './models';
import { AG_GRID_WRAPPER_HTML_ID, useVisualizationTable } from './utils';

ModuleRegistry.registerModules([AllEnterpriseModule]);
LicenseManager.setLicenseKey(process.env.NEXT_PUBLIC_AG_GRID_LICENSE ?? '');

const Table = ({ options, executionId, activeControls, params, data, meta, editing }: VisualizationTableProps) => {
    const { enableSearch, search, features, datasource, agGridProps, columnDefs, handleSearch, gridRef } =
        useVisualizationTable({
            options,
            activeControls,
            params,
            executionId,
            data,
            meta,
            editing,
        });

    return (
        <FlexBox col gap="md" w="full" h="full" id={AG_GRID_WRAPPER_HTML_ID}>
            <style>
                {`
                    /* Remove the the column drag handle if it's disabled */
                    #${AG_GRID_WRAPPER_HTML_ID} .ag-column-select-column-drag-handle.ag-column-select-column-readonly {
                        display: none;
                    }
                `}
            </style>
            <AgGridReact ref={gridRef} columnDefs={columnDefs} serverSideDatasource={datasource} {...agGridProps} />
            {enableSearch && (
                <FlexBox
                    id={TABLE_FOOTER_HTML_ID}
                    className={cn('w-[200px] min-w-[200px]')}
                    style={{ height: TABLE_FOOTER_HEIGHT }}
                >
                    <Input
                        w="full"
                        variant="outline-fill"
                        size="sm"
                        placeholder="Search..."
                        onChange={handleSearch}
                        debounce={350}
                        value={search}
                        /** this will disable the input for public dashboards. maybe just hide it? */
                        disabled={!features.tableSearch}
                    />
                </FlexBox>
            )}
        </FlexBox>
    );
};

type LiteTableProps = Pick<VisualizationTableProps, 'options' | 'meta' | 'executionId' | 'data'>;

const LiteTable = (props: LiteTableProps) => {
    return <Table {...(props as VisualizationTableProps)} />;
};

/**
 * A light version of the table component that renders the same table view but expects less props than our internal viz engine component
 * and skips the initial fetch of the execution data, so the parent can be responsible for the initial fetch. Then the internal component
 * can be responsible for paginated requests
 */
Table.Lite = LiteTable;

export { Table };
